import { useRef, useState } from "react";
import { Box, Button, Flex, IconButton, Text } from "@radix-ui/themes";
import { IconPlus, IconTrash, IconFile } from "@tabler/icons-react";
import * as Dialog from "@/components/CustomUiComponents/Dialog";

interface CampaignMassAddCreatorProps {
  mobile?: boolean;
  mutate: () => void;
  campaignId: number;
}

const CampaignMassAddCreator = ({
  mobile,
  mutate,
  campaignId,
}: CampaignMassAddCreatorProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(null);
    } else {
      clearFile();
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const add = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `/backend/campaign/${campaignId}/mass-budget`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.result?.error || data.message || "Failed to upload CSV");
      }

      setFile(null);
      clearFile();
      mutate();

      // Show success message with details
      const results = data.data?.results || {
        created: 0,
        skipped: 0,
        errors: [],
      };
      let successMsg = `Successfully processed CSV! `;
      if (results.created > 0)
        successMsg += `${results.created} creator(s) added. `;
      if (results.skipped > 0)
        successMsg += `${results.skipped} skipped (already exist). `;
      if (results.errors && results.errors.length > 0)
        successMsg += `${results.errors.length} error(s).`;

      setSuccess(successMsg);

      // Auto-close after 2 seconds if successful with no errors
      if (!results.errors || results.errors.length === 0) {
        setTimeout(() => {
          const closeButton = document.querySelector(
            "[data-dialog-close]"
          ) as HTMLElement;
          closeButton?.click();
        }, 2000);
      }
    } catch (err: any) {
      console.error("Error uploading CSV:", err);
      setError(err.message || "Failed to upload CSV file. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        {mobile ? (
          <IconButton>
            <IconPlus className="icon-accent" />
            CSV
          </IconButton>
        ) : (
          <Button style={{ width: "160px" }}>
            <IconFile className="icon-accent" />
            Import CSV
          </Button>
        )}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content
          style={{ width: mobile ? "100vw" : "600px", maxWidth: "none" }}
        >
          <Dialog.Title>Add Creators to Campaign from CSV</Dialog.Title>

          <Flex direction="column" gap="3" mt="4">
            <Box>
              <Text size="2" mb="2">
                Upload your CSV file with creator information. Creators will be
                added to the database if they don't exist, then added to this
                campaign's budget.
              </Text>
              <br />
              <Text size="1" color="gray" mb="2">
                Required: NAME, PLATFORM, USERNAME (@)
                <br />
                Optional: PRICE, CONTACT TYPE, CONTACT INFO, POST FORMAT, IS
                MASS POSTER, NUMBER OF POSTS
                <br />
                (NUMBER OF POSTS sets the budget, defaults to 1)
              </Text>
              <Flex align="center" gap="3" style={{ marginTop: 10 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                />
                {file && (
                  <IconButton variant="soft" color="gray" onClick={clearFile}>
                    <IconTrash size={16} />
                  </IconButton>
                )}
              </Flex>
              {error && (
                <Text color="red" size="2" mt="2">
                  {error}
                </Text>
              )}
              {success && (
                <Text color="green" size="2" mt="2">
                  {success}
                </Text>
              )}
            </Box>
          </Flex>

          <Flex direction="row" gap="3" mt="4" justify="end">
            <Dialog.Close data-dialog-close>
              <Button variant="outline">Cancel</Button>
            </Dialog.Close>
            <Button onClick={add} disabled={!file || isLoading}>
              {isLoading ? "Uploading..." : "Submit"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CampaignMassAddCreator;
